import { useState, useEffect, useCallback } from "react";
import {
  getUserDocuments,
  addDocument,
  updateDocument,
  deleteDocument,
  duplicateDocument,
  DocumentHistory,
} from "../services/documentHistoryService";

export function useDocumentHistory(userId: string) {
  const [documents, setDocuments] = useState<DocumentHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await getUserDocuments(userId);
      setDocuments(docs);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        setError(
          (err as { message: string }).message || "Error loading documents"
        );
      } else {
        setError("Error loading documents");
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadDocuments();
  }, [userId, loadDocuments]);

  const download = (doc: DocumentHistory) => {
    const plainText = doc.content.replace(/[#*_`~>-]/g, "");
    const element = document.createElement("a");
    const file = new Blob([plainText], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    element.href = URL.createObjectURL(file);
    element.download = `${doc.type}_${new Date(doc.date).toISOString()}.docx`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const share = async (doc: DocumentHistory) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Documento Legal - ${doc.title}`,
          text: doc.content,
          url: doc.url || window.location.href,
        });
      } else {
        // If there's a URL from backend, copy that; otherwise copy content
        const shareText = doc.url ? doc.url : doc.content;
        await navigator.clipboard.writeText(shareText);
        // Use a more user-friendly notification
        if (typeof window !== "undefined" && "toast" in window) {
          (window as any).toast?.success(
            doc.url
              ? "Enlace del documento copiado al portapapeles"
              : "Documento copiado al portapapeles"
          );
        } else {
          alert(
            doc.url
              ? "Enlace del documento copiado al portapapeles"
              : "Documento copiado al portapapeles"
          );
        }
      }
    } catch (err) {
      if (typeof window !== "undefined" && "toast" in window) {
        (window as any).toast?.error("Error al compartir documento");
      } else {
        alert("Error al compartir documento");
      }
    }
  };

  const view = (
    doc: DocumentHistory,
    setPreview: (content: string) => void
  ) => {
    setPreview(doc.content);
  };

  const edit = (
    doc: DocumentHistory,
    setForm: (doc: DocumentHistory) => void
  ) => {
    setForm(doc);
  };

  const duplicate = async (doc: DocumentHistory) => {
    await duplicateDocument(doc.id, doc.user_id);
    await loadDocuments();
  };

  const remove = async (doc: DocumentHistory) => {
    await deleteDocument(doc.id);
    await loadDocuments();
  };

  return {
    documents,
    loading,
    error,
    reload: loadDocuments,
    download,
    share,
    view,
    edit,
    duplicate,
    remove,
  };
}
